<template>
		<anchor-element v-if="visible" tag="div" :chain="chain" class="fd-mail-select fd-input-like" :class="[clazz, errorClass, extraClasses]"
						:style="style"
						:title="mailSelectTitle"
						:tabindex="0" @focus="onRootFocus" @blur="onRootBlur"
						@keydown="onRootKeyDown"
						ref="anchor">
			<template v-for="(item) in wrapSegmentPairs" :key="item.key">
				<span v-if="item.q && item.p" class="us-pair">
					<tag-input v-if="segmentIsQuery(item.q)" :min-width="item.q.last ? 32 : 6" class="upq" v-model="item.q.query"
							   @focus="onQueryFocus(item.q.index)" :tabindex="0"
							   @blur="onQueryBlur(item.q.index)"
							   @paste="pasteValue"
							   @keydown="onKeyDown($event, item.q.index)"></tag-input>
					<mail-tag v-if="segmentIsMail(item.p)" :segment="item.p" :disabled="ro" @click="selectClick($event,item.p)"
							  @update="(newAddress:string) => onUpdateAddress(item.p.index, newAddress)" @remove="onRemoveAddress(item.p.index)"></mail-tag>
				</span>
				<tag-input v-else-if="segmentIsQuery(item.q)" :min-width="item.q.last ? 32 : 6" class="upq" v-model="item.q.query"
							:tabindex="0"
						   @focus="onQueryFocus(item.q.index)"
						   @blur="onQueryBlur(item.q.index)"
						   @paste="pasteValue"
						   @keydown="onKeyDown($event, item.q.index)"></tag-input>
				<mail-tag v-else-if="segmentIsMail(item.p)" :segment="item.p" :disabled="ro" @click="selectClick($event,item.p)"
						  @update="(newAddress:string) => onUpdateAddress(item.p.index, newAddress)" @remove="onRemoveAddress(item.p.index)"></mail-tag>
			</template>
		</anchor-element>
		<boat-element tag="div" :chain="chain" class="fd-drop fd-mail-select-drop" ref="boat">
			<template v-for="si in suggestions.mails" :key="si.id">
				<mail-suggestion :item="si" :selected="si.id === selectedSuggestion"
								@mouseenter="suggestionMouseEnter($event, si)"
								@mouseleave="suggestionMouseLeave($event, si)"
								@click="addSuggestion(si)"></mail-suggestion>
			</template>
			<div v-if="hasMoreSuggestions" class="ms-load-more" @click="loadMoreSuggestions"><sym-icon name="caret-down-14"></sym-icon><span>{{ env.msg('suggester.more') }}</span></div>
		</boat-element>
		<!--div style="font-size:10px">focusedQueryIndex: {{ focusedQueryIndex }}, lastFocusedQueryIndex: {{ lastFocusedQueryIndex }},
			rootFocused: {{ rootFocused }}, focused: {{ focused }}, selectedSuggestion: {{ selectedSuggestion }}</div-->
</template>

<script setup lang="ts">
import {ref, computed, nextTick, watchEffect, watch} from "vue";
import useEditor, {type EditorProps, editorProps} from "@fd/editors/Editor.ts";
import {SuggestedMail, MailSelect, MailResponse, SUGGESTIONS_LIMIT} from "@t/fd/editor/MailSelect.ts";
import {ChainRule, createChain} from "@/ui/paneltree/PanelTree.ts";
import {MailSegment, MSegment, QuerySegment, SegmentWrapPair, useMailSegmentTags} from "@fd/editors/mail-select/MailSelectUtils.ts";
import MailTag from "@fd/editors/mail-select/MailTag.vue";
import TagInput from "@fd/editors/suggester/TagInput.vue";
import {connectEventBus, injectEnv} from "@fd/Environment.ts";
import MailSuggestion from "@fd/editors/mail-select/MailSuggestion.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import {calmTrigger} from "@/utils/Debounce.ts";
import {ElementMeasure} from "@fd/helpers/DomUtils.ts";
import {addressToString, InternetAddress, parseAddresses, validAddressList} from "@t/mail/InternetAddress.ts";
import {caretAtEnd, caretAtStart, getTextLength, hasSelection, isBlank} from "@fd/editors/suggester/ContentEditableUtils.ts";
import {addContextParams, injectContext, MAIL_SELECT_REFS} from "@t/fd/AppContext.ts";
import SymIcon from "@fd/helpers/SymIcon.vue";

const uprops = defineProps({...editorProps});
const props = uprops as EditorProps;
const env = injectEnv();

defineExpose({focus});
const bus = connectEventBus();
const ctx = injectContext();

/** The part */
const us = computed<MailSelect>(() => props.part as MailSelect);
const {visible, value, localData, clazz, style, ro, errorClass, errorMessage, getValueOfProperty, setValueOfProperty} = useEditor(props);

// If a query is focused, this will be the index, -1 otherwise
const focusedQueryIndex = ref<number>(-1);
// contains last focus index event if the focus is already lost
const lastFocusedQueryIndex = ref<number>(-1);
// true if the whole component is focused (not a query)
const rootFocused = ref<boolean>(false);
const queryFocused = computed<boolean>(() => focusedQueryIndex.value >= 0);
const focused = computed<boolean>(() => rootFocused.value || queryFocused.value );
/** This variable blocks calls to the backend until the first focus happens */
const sleep = ref<boolean>(true);

const anchor = ref<InstanceType<typeof AnchorElement> | null>(null);
const boat = ref<InstanceType<typeof BoatElement> | null>(null);
const rootElement = computed<HTMLElement|null>(() => anchor.value ? anchor.value.element() : null);

const extraClasses = computed(() => {
	const c: string[] = [];
	if (ro.value) c.push("fd-mail-select-disabled");
	if (!allowQueries.value) c.push("fd-mail-select-no-queries");
	return c;
});

const mailSelectTitle = computed<string>(()=>{
	return errorMessage.value;
});

const { mailTags, contains, count, unselectAll, toggleEmailSelection, selectEmail, selectAll } = useMailSegmentTags(env, value);

const canAdd = computed(() => {
	return typeof us.value.max === "number" ? count.value < us.value.max : true;
});

const canDelete = computed(() => {
	return typeof us.value.min === "number" ? count.value > us.value.min : count.value > 0;
});

const allowQueries = computed(() => { return !ro.value && canAdd.value; });

// Queries go here
// All query editors are stored here. There should be array.length + 1 items
const editors = ref<QuerySegment[]>([]);

// Current list of loaded suggestions
const suggestions = ref<MailResponse>({
	mails: []
});
// Number of suggestions currently loaded
const suggestionCount = computed<number>(() => {
	return suggestions.value?.mails?.length ?? 0;
});
// This suggestion will be highlighted in the drop-down
const selectedSuggestion = ref<string>("");	// ID of a selected suggestion

/** Auctual query updated as the user types */
const query = ref<string>("");

const above:ChainRule = {
	ah: 0, av: 0,
	bh: 0, bv: 100,
	x: -2, y: -3 };
const bellow:ChainRule = {
	ah: 0, av: 100,
	bh: 0, bv: 0,
	x: -2, y: 3 };

const chain = createChain({
	managed: true,
	rule: (m:ElementMeasure) => {
		if (m.spaceBottom >= 300 || m.spaceBottom > m.spaceTop) {
			return { ...bellow, spaceLimit: m.spaceBottom };
		} else {
			return { ...above, spaceLimit: m.spaceTop };
		}
	},
}, "mail-select");

const dropVisible = computed<boolean>(() => {
	return canAdd.value && (chain.interacting.value || queryFocused.value) && suggestionCount.value > 0;
});


const selectedTagCount = computed<number>(() => {
	let count:number = 0;
	for (let t of mailTags.value) {
		if (t.selected) count++;
	}
	return count;
});

const anyTagSelected = computed<boolean>(() => {
	return selectedTagCount.value > 0;
});

const firstSelectedTagIndex = computed<number>(() => {
	for (let i = 0; i < mailTags.value.length; i++) {
		if (mailTags.value[i].selected) return i;
	}
	return -1;
});

const lastSelectedTagIndex = computed<number>(() => {
	for (let i = mailTags.value.length - 1; i >= 0; i--) {
		if (mailTags.value[i].selected) return i;
	}
	return -1;
});

const limit = ref<number>(SUGGESTIONS_LIMIT);

const hasMoreSuggestions = computed<boolean>(() => suggestions.value.mails.length > 0 && suggestions.value.mails.length >= limit.value);

function loadMoreSuggestions(e:MouseEvent) {
	limit.value += SUGGESTIONS_LIMIT;
	e.preventDefault();
	e.stopPropagation();
	if (lastFocusedQueryIndex.value >= 0) {
		focusQuery(lastFocusedQueryIndex.value);
	}
}

const endpoint = computed<string>(() => {
	if (ro.value || sleep.value || !queryFocused.value) return "";
	let ep = "api/mails/suggest";
	ep = env.net.urlParam(ep, "q", query.value);
	ep = env.net.urlParam(ep, "limit", limit.value);
	ep = addContextParams(ep, ctx.value, MAIL_SELECT_REFS);

	const exclude:Set<string> = new Set<string>();
	for (let item of mailTags.value) {
		exclude.add(item.address.toLowerCase());
	}
	if (exclude.size > 0) ep = env.net.urlParam(ep, "exclude", [...exclude].join(','));
	return ep;
});

function nameMatchQuery(name:string, query:string):boolean {
	const n = name.toLowerCase();
	const q = query.toLowerCase();
	return n.indexOf(q) >= 0;
}

async function loadSuggestions() {
	let ep = endpoint.value;
	if (ep.length === 0) return;
	const resp = await env.net.fetch(ep);
	if (resp.ok) {
		suggestions.value = await resp.json() as MailResponse;
	} else {
		suggestions.value = { mails: [] };
	}
	selectFirstSuggestion();
}

const debouncedLoadSuggestions = calmTrigger(loadSuggestions, 150);

watchEffect(() => {
	if (endpoint.value.length > 0) debouncedLoadSuggestions();
});

const suggestionIds = computed<string[]>(() => {
	const ids:string[] = [];
	if (Array.isArray(suggestions.value?.mails)) {
		for (let s of suggestions.value.mails) ids.push(s.id);
	}
	return ids;
});

function suggestionMouseEnter(e:MouseEvent, s:SuggestedMail):void {
	selectedSuggestion.value = s.id;
}

function suggestionMouseLeave(e:MouseEvent, s:SuggestedMail):void {
	selectedSuggestion.value = "";
}

function selectFirstSuggestion() {
	const ids = suggestionIds.value;
	if (ids.length > 0) selectedSuggestion.value = ids[0];
	else selectedSuggestion.value = "";
}

function selectLastSuggestion() {
	const ids = suggestionIds.value;
	if (ids.length > 0) selectedSuggestion.value = ids[0];
	else selectedSuggestion.value = "";
}

function selectNextSuggestion() {
	try {
		const ids = suggestionIds.value;
		for (let i = 0; i < ids.length; i++) {
			if (selectedSuggestion.value === ids[i]) {
				if (i + 1 < ids.length) selectedSuggestion.value = ids[i + 1];
				else selectedSuggestion.value = ids[0];
				return;
			}
		}
		selectFirstSuggestion();
	} finally {
		ensureSelectionVisible();
	}
}

function selectPrevSuggestion() {
	try {
		const ids = suggestionIds.value;
		for (let i = 0; i < ids.length; i++) {
			if (selectedSuggestion.value === ids[i]) {
				if (i > 0) selectedSuggestion.value = ids[i - 1];
				else selectedSuggestion.value = ids[ids.length - 1];
				return;
			}
		}
		selectLastSuggestion();
	} finally {
		ensureSelectionVisible();
	}
}

function ensureSelectionVisible() {
	nextTick(() => {
		if (boat.value) {
			const bel = boat.value.element();
			if (bel) {
				bel.querySelector(".us-item-selected")?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
			}
		}
	});
}

const wrapSegmentPairs = computed<SegmentWrapPair[]>(() => {
	const pairs:SegmentWrapPair[] = [];
	let edIdx = 0;
	const aq = allowQueries.value;
	for (let tag of mailTags.value) {
		const pair: SegmentWrapPair = { key: tag.key };
		if (aq) pair.q = notLast(queryAt(edIdx++));
		pair.p = tag;
		pairs.push(pair);
	}
	if (aq) {
		const qed = last(queryAt(edIdx));
		pairs.push({ q: qed, key: qed.key });
	}
	return pairs;

});

const segmentId = ref(0);
/* Key generating - for efficient list rendering */
function nextId(): string {
	return "seg" + ++segmentId.value;
}

function queryAt(idx: number): QuerySegment {
	while (idx >= editors.value.length) editors.value.push(newQuery(idx));
	return editors.value[idx];
}

function insertQueryAt(idx:number):QuerySegment {
	if (idx < editors.value.length) {
		let qs = newQuery(idx);
		editors.value.splice(idx, 0, qs);
		for (let i = idx+1; i < editors.value.length; i++) editors.value[i].index = i;	// correct indexes after the new inserted query segment
	} else return queryAt(idx);
}

function mergeQueryAt(idx:number):QuerySegment {
	if (idx < (editors.value.length - 1)) {
		// Merge possible
		const q = editors.value[idx];
		const qNext = editors.value[idx+1];
		q.query = q.query + qNext.query;
		q.last = qNext.last;
		editors.value.splice(idx + 1, 1);
		for (let i = idx+1; i < editors.value.length; i++) editors.value[i].index = i;
		return q;
	}
	return null;
}

function emptyAllQueries():void {
	for (let q of editors.value) {
		q.query = "";
	}
}

function last(qs:QuerySegment):QuerySegment {
	qs.last = true;
	return qs;
}

function notLast(qs:QuerySegment):QuerySegment {
	qs.last = false;
	return qs;
}

function newQuery(idx: number): QuerySegment {
	return {
		key: nextId(),
		type: "query",
		query: "",
		index: idx
	};
}


function segmentIsQuery(segment: MSegment): segment is QuerySegment {
	return segment?.type === 'query';
}

function segmentIsMail(segment: MSegment): segment is MailSegment {
	return segment?.type === 'mail';
}

function resetAllQueries() {
	for (let q of editors.value) {
		q.query = "";
	}
}

function copySelected(cut = false):boolean {
	if (cut && (ro.value || !canDelete.value)) return false;
	const result:string[] = [];
	for (let tag of mailTags.value) {
		if (tag.selected && tag.address) result.push(addressToString(tag));
	}
	if (result.length > 0) {
		navigator.clipboard.writeText(result.join(", "));
	}
	if (cut) {
		return deleteSelected(true);
	}
}

/** Return the indexes of selected items in normal or reverse order */
function selectedIndexes(descOrder = false): number[] {
	const idxs:number[] = [];
	let idx = 0;
	for (let tag of mailTags.value) {
		if (tag.selected) {
			if (descOrder) idxs.splice(0, 0, idx);
			else idxs.push(idx);
		}
		idx++;
	}
	return idxs;
}


function selectedValues():(string)[] {
	const ids:(string)[] = [];
	for (let tag of mailTags.value) {
		if (tag.selected) ids.push(tag.key);
	}
	return ids;
}

function selectedNormalizedValues():string[] {
	const ids:string[] = [];
	for (let tag of mailTags.value) {
		if (tag.selected) ids.push(tag.address);
	}
	// Deduplicate
	return [...new Set(ids)];
}

function deleteAddressAt(index:number):void {
	value.value.splice(index, 1);
	mergeQueryAt(index);
	unselectAll();
}

/**
 * Delete selected items and returns true if the operation has changed the underlying value
 */
function deleteSelected(doFocus = true):boolean {
	if (ro.value || !canDelete.value) return false;
	const sidxs = selectedIndexes(true);
	if (sidxs.length === 0 || (typeof us.value.min === "number" && (value.value.length - sidxs.length) < us.value.min)) return false;	// cannot remove under the min
	for (let i = 0; i < sidxs.length; i++) {
		deleteAddressAt(sidxs[i]);
	}
	unselectAll();
	if (doFocus) {
		const focusIndex = sidxs[0] - sidxs.length + 1;
		focusQuery(focusIndex);
	}
	return true;
}

function findSuggestion(id: string):SuggestedMail|null {
	if (Array.isArray(suggestions.value.mails) && suggestions.value.mails.length > 0) {
		for (let s of suggestions.value.mails) {
			if (s.id === id) return s ;
		}
	}
	return null;
}


function addSuggestion(sup:SuggestedMail, doFocus = false):boolean {
	// Find addition index
	let idx:number;
	if (focusedQueryIndex.value >= 0) idx = focusedQueryIndex.value;
	else if (lastFocusedQueryIndex.value >= 0) idx = lastFocusedQueryIndex.value;
	else idx = mailTags.value.length;

	let anyInserted = false;
	for (let a of sup.mails) {
		if (addAddressAt(idx, a)) {
			idx++;
			anyInserted = true;
		}
	}
	if (anyInserted) {
		propagate(sup);
		emptyAllQueries();
	}
	if (doFocus) {
		//console.log("....doing focus allowQueries="+allowQueries.value);
		if (allowQueries.value) focus(idx);
	}
	return anyInserted;
}

function addSuggestionByEnter():void {
	if (selectedSuggestion.value !== "") {
		const s = findSuggestion(selectedSuggestion.value);
		if (s) addSuggestion(s, true);
	}
}

/** Adds an address at given position and returns true if the address was really added */
function addAddressAt(index:number, a:InternetAddress):boolean {
	if (!canAdd.value || contains(a.address)) return false;	// not allowed

	if (index < 0 || index > value.value.length) throw new Error("Invalid index for adding an address: "+index+", should be [0..."+value.value.length+"]");

	if (!Array.isArray(value.value)) { // init new array if not already
		value.value =  [];
	}
	value.value.splice(index, 0, a);
	// Update stored indexes
	if (focusedQueryIndex.value >= index) focusedQueryIndex.value = focusedQueryIndex.value + 1;
	if (lastFocusedQueryIndex.value >= index) lastFocusedQueryIndex.value = lastFocusedQueryIndex.value + 1;
	// Update editors
	insertQueryAt(index);
	return true;
}


function focus(insertIndex?: number):void {
	let index = insertIndex || 0;
	focusQuery(index);
}

function selectClick(e: MouseEvent, segment:MailSegment):void {
	e.preventDefault();
	if (!e.shiftKey) unselectAll();
	toggleEmailSelection(segment.address);
	sleep.value = false;
}

function selectTag(idx: number, unselectOthers = false):void {
	if (unselectOthers) unselectAll();
	const tag = mailTags.value[idx];
	selectEmail(tag.address);
}

let stopQueryWatcher = null;

function onQueryFocus(idx: number) {
	if (stopQueryWatcher) {
		stopQueryWatcher();
		stopQueryWatcher = null;
	}

	const q = queryAt(idx);
	stopQueryWatcher = watch(() => q.query, (val) => {
		query.value = val.trim();
	}, { immediate: true });

	unselectAll();
	focusedQueryIndex.value = idx;
	sleep.value = false;
}

function onQueryBlur(idx: number) {
	// stop updating query.value
	if (stopQueryWatcher) {
		stopQueryWatcher();
		stopQueryWatcher = null;
	}
	const q = query.value;
	if (validEmailOrAlias(q)) {
		addSuggestion(toSuggestedMail(q));
	}

	if (focusedQueryIndex.value >= 0) lastFocusedQueryIndex.value = focusedQueryIndex.value;
	focusedQueryIndex.value = -1;
	selectedSuggestion.value = "";
}

function onRootFocus(e:FocusEvent) {
	rootFocused.value = true;
}
function onRootBlur() {
	rootFocused.value = false;
}

function onUpdateAddress(index:number, email:string):void {
	if (index >= 0 && index < value.value.length) {
		const item = value.value[index] as (InternetAddress|string);
		if (typeof item === 'string') value.value[index] = email;
		else item.address = email;
	}
}

function onRemoveAddress(index:number):void {
	deleteAddressAt(index);
}

/** Move cursor to the query with given index */
function focusQuery(idx: number) {
	if (!allowQueries.value) focusRoot();
	else {
		nextTick(() => {
			if (rootElement.value) {
				const qelms = rootElement.value.querySelectorAll(".upq");
				if (idx < qelms.length) (qelms[idx] as HTMLElement).focus();
			}
		});
	}
}

function focusRoot() {
	//console.log("plan to focus component");
	focusedQueryIndex.value = -1;
	nextTick(() => {
		//console.log("next tick focus component");
		if (rootElement.value) {
			//console.log("next tick calling focus");
			rootElement.value.focus();
		}
	});
}

function focusQuerySibling(index: number, prev = false):boolean {
	if (prev) {
		if (index <= 0) return false;
		selectTag(index - 1, true);
		focusRoot();
	} else {
		if (index >= mailTags.value.length) return false;
		selectTag(index, true);
		focusRoot();
	}
	return true;
}

function focusTagSibling(prev = false):boolean {
	if (selectedTagCount.value === 0) {
		if (prev || !canAdd.value) return false;
		focusQuery(0);
		return true;
	}

	if (mailTags.value.length > 0 && selectedTagCount.value < mailTags.value.length) {
		if (canAdd.value) {
			// Will jump to the next query
			let idx;
			if (selectedTagCount.value === 0) {
				idx = prev ? mailTags.value.length : 0;
			} else if (selectedTagCount.value < mailTags.value.length) {
				if (prev) {
					idx = firstSelectedTagIndex.value;
					if (idx < 0) idx = mailTags.value.length;
				} else {
					idx = lastSelectedTagIndex.value + 1;
					if (idx > mailTags.value.length) idx = 0;
				}
			}
			unselectAll();
			focusQuery(idx);
			return true;
		} else {
			// Will jump to the next tag
			let idx;
			if (selectedTagCount.value === 0) {
				idx = prev ? mailTags.value.length - 1 : 0;
			} else if (selectedTagCount.value < mailTags.value.length) {
				if (prev) {
					idx = firstSelectedTagIndex.value - 1;
					if (idx < 0) idx = mailTags.value.length - 1;
				} else {
					idx = lastSelectedTagIndex.value + 1;
					if (idx >= mailTags.value.length) idx = 0;
				}
			}
			const t = mailTags.value[idx];
			unselectAll();
			selectEmail(t.address);
			return true;
		}
	}
	return false;
}

function onRootKeyDown(e:KeyboardEvent) {
	//console.log("onRootKeyDown", e.key, "shift="+e.shiftKey, "ctrl="+e.ctrlKey, "meta="+e.metaKey);
	if (e.key === "Backspace" || e.key === "Delete") {
		if (deleteSelected()) e.preventDefault();
	} else if ((e.key === "c" || e.key === "x") && (e.ctrlKey || e.metaKey)) {
		// Copy selected addresses
		if (focusedQueryIndex.value < 0) {
			if (copySelected(e.key === "x")) e.preventDefault();
		}
	} else if (e.key === "Tab") {
		if (focusTagSibling(e.shiftKey)) e.preventDefault();
	} else if (e.key === "ArrowLeft") {
		if (focusTagSibling(true)) e.preventDefault();
	} else if (e.key === "ArrowRight") {
		if (focusTagSibling(false)) e.preventDefault();
	}
}

/** User typed inside a query */
function onKeyDown(e: KeyboardEvent, index:number) {
	//console.log("onKeyDown", e.key, "shift="+e.shiftKey, "ctrl="+e.ctrlKey, "meta="+e.metaKey);
	if (e.key === ' ') {
		// Suppress space at start of the query
		if (caretAtStart(e.target as HTMLElement)) e.preventDefault();
	} else if (e.key === "Backspace") {
		if (caretAtStart(e.target as HTMLElement) && !hasSelection()) {
			if (index > 0) {
				// There is a tag before this
				unselectAll();
				selectEmail(mailTags.value[index-1].address);
				focusRoot();
			}
			e.preventDefault();
			e.stopPropagation();
		}
	} else if (e.key === "Delete") {
		if (caretAtEnd(e.target as HTMLElement) && !hasSelection()) {
			if (index < mailTags.value.length) {
				unselectAll();
				selectEmail(mailTags.value[index].address);
				// Focus parent
				focusRoot();
			}
			e.preventDefault();
			e.stopPropagation();
		}
	} else if (e.key === "a" && (e.ctrlKey || e.metaKey)) {
		// Select-all action
		if (isBlank(e.target as HTMLElement)) {
			e.preventDefault();
			e.stopPropagation();
			selectAll();
			focusRoot();
		}
	} else if (e.key === "Tab") {
		e.stopPropagation();
		if (addAddressFromQuery(e, index, true)) {
			e.preventDefault();
		} else {
			if (focusQuerySibling(index, e.shiftKey)) {
				e.preventDefault();
			}
		}
	} else if (e.key === "ArrowUp") {
		if (suggestionCount.value > 0) {
			selectPrevSuggestion();
			e.preventDefault();
		}
	} else if (e.key === "ArrowDown") {
		if (suggestionCount.value > 0) {
			selectNextSuggestion();
			e.preventDefault();
		}
	} else if (e.key === "ArrowLeft") {
		e.stopPropagation();
		if (index > 0 && isBlank(e.target as HTMLElement)) {
			if (focusQuerySibling(index, true)) {
				e.preventDefault();
			}
		}
	} else if (e.key === "ArrowRight") {
		e.stopPropagation();
		if (index < editors.value.length-1 && isBlank(e.target as HTMLElement)) {
			if (focusQuerySibling(index, false)) {
				e.preventDefault();
			}
		}
	} else if (e.key === "Enter") {
		if (suggestionCount.value > 0) {
			addSuggestionByEnter();
			e.preventDefault();
		} else {
			addAddressFromQuery(e, index, true);
		}
	} else if (e.key === ",") {
		addAddressFromQuery(e, index, true);
	}
}

function addAddressFromQuery(e: KeyboardEvent, index:number, prevent: boolean): boolean {
	if (prevent) e.preventDefault();
	if (validEmailOrAlias(query.value)) {
		return addSuggestion(toSuggestedMail(query.value));
	}
	return false;
}

function validEmailOrAlias(email: string): boolean {
	const regex = /^[^\s@]+@[^\s@]+$/;
	return regex.test(email);
}

function toSuggestedMail(query: string): SuggestedMail {
	return {
		id: query,
		customer: 0,
		mails: [{address: query}],
		name: null,
		icon: "anonymous"
	};
}

function pasteValue(e: ClipboardEvent) {
	const text = e.clipboardData.getData("text/plain");
	if (validAddressList(text)) {
		// Looks like addresses
		const parsed = parseAddresses(text);
		let idx;
		if (focusedQueryIndex.value >= 0) idx = focusedQueryIndex.value;
		else idx = mailTags.value.length;
		for (let a of parsed) {
			if (addAddressAt(idx, a)) idx++;
		}
		focusQuery(idx);
		e.preventDefault();
	}
}

let visibilityTimeout = 0;
function chainVisible() { chain.visible.value = true; visibilityTimeout = 0; }
function chainHidden() { chain.visible.value = false; visibilityTimeout = 0; }

function setDropVisibility(visible: boolean) {
	if (visibilityTimeout) clearTimeout(visibilityTimeout);
	visibilityTimeout = setTimeout(visible ? chainVisible : chainHidden, 100);
}

watchEffect(() => { setDropVisibility(dropVisible.value); });

/** Unselect all tags on a long blur **/

const MAX_BLUR_MILLIS = 1000;

const keepSelection = computed<boolean>(() => {
	return chain.interacting.value || focused.value;
});

let unselectionTimeout = 0;

watch(keepSelection, (s:boolean) => {
	if (s) {
		if (unselectionTimeout) {
			clearTimeout(unselectionTimeout);
			unselectionTimeout = 0;
		}
	} else {
		// Blurred
		unselectionTimeout = setTimeout(() => {
			unselectAll();
			resetAllQueries();
			limit.value = SUGGESTIONS_LIMIT;
		}, MAX_BLUR_MILLIS);
	}
});


//propagate 
function propagate(sup:SuggestedMail) {
	if (sup.customer || sup.branch || sup.person) { //propagate party values if exists
		bus.fire("propagatetrinity", {
			customer: sup.customer ?? 0,
			branch: sup.branch ?? 0,
			person: sup.person ?? 0
		});
	}
}

</script>

<style>
.fd-mail-select {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: baseline;
	flex-wrap: wrap;
	column-gap: 0; /* should be filled with the editor */
	row-gap: 1px;

	/* padding: 0 var(--input-h-padding); */
	padding:0;
	min-height: var(--input-height);
	background-color: var(--bg);
	color: var(--fg);
	margin: 0;
	font-size: var(--input-font-size);
	line-height: var(--input-height);
	font-family: var(--font);
	cursor: default;
	width: 100%;

	&.fd-mail-select-disabled {
		background-color: var(--input-disabled-bg);
		color: var(--input-disabled-fg);
	}
	&.fd-mail-select-no-queries {
		column-gap: 6px;
		padding-inline: var(--input-h-padding);
	}
	
	&.invalid {
		color: var(--error-fg);
		background-color: var(--input-error-bg);
	}

	/*&:focus-visible, &:focus {
		outline: 2px solid greenyellow !important;
	}*/
}

.upq {
	/* Query less visible than normal text */
	color: color-mix(in srgb, var(--fg) 66%, transparent);
}

.fd-mail-select:focus {
    outline: none;
}

.fd-mail-select-drop {
	border-radius: calc(var(--input-radius) + 2px);
	width: min(calc(var(--anchor-width) + 4px), 500px);
	max-height: calc(var(--space-limit) - 6px);
	overflow-y: auto;

	& > .us-not-found {
		padding: 8px 8px 0 8px;
		opacity: 0.5;
	}
}

/** Load more suggester row */
.ms-load-more {
	padding: 4px 8px 4px 0;
	opacity: 0.5;
	cursor: pointer;
	-webkit-user-select: none;
	user-select: none;

	&:hover {
		opacity: 1;
		background-color: var(--drop-bg-selection);
		color: var(--drop-fg-selection);
	}
	& > .fi {
		width: 30px;
	}
}
</style>